import { AlertTriangle, RefreshCw } from 'lucide-react';

interface QueryErrorBannerProps {
  error: Error | null;
  onRetry?: () => void;
  message?: string;
  compact?: boolean;
}

/**
 * Reusable error banner for failed React Query requests.
 * Shows a user-friendly error message with retry button.
 *
 * Usage:
 *   const { data, isLoading, isError, error, refetch } = useQuery({...});
 *   {isError && <QueryErrorBanner error={error} onRetry={refetch} />}
 */
export function QueryErrorBanner({
  error,
  onRetry,
  message,
  compact = false,
}: QueryErrorBannerProps) {
  const errorMessage =
    message ||
    ((error as any)?.isTimeout
      ? 'The request timed out. The server may be busy.'
      : (error as any)?.isNetworkError
        ? 'Unable to connect to the server. Check your connection.'
        : error?.message || 'An unexpected error occurred.');

  if (compact) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm">
        <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
        <span className="text-red-700 dark:text-red-300 flex-1">
          {errorMessage}
        </span>
        {onRetry && (
          <button
            onClick={onRetry}
            className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 bg-white dark:bg-slate-800 rounded-xl border border-red-200 dark:border-red-800">
      <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center mb-4">
        <AlertTriangle className="w-8 h-8 text-red-500" />
      </div>
      <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">
        Something went wrong
      </h3>
      <p className="text-sm text-slate-500 dark:text-slate-400 text-center max-w-sm mb-4">
        {errorMessage}
      </p>
      {onRetry && (
        <button
          onClick={onRetry}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Retry
        </button>
      )}
    </div>
  );
}
